// Scroll the first validation error into view after a failed submit.
//
// Why a helper: the checkout app mounts inside the <m7-checkout> custom
// element's SHADOW ROOT, so `document.querySelector('.text-scarlet')` can't
// see the error nodes (they live in the shadow tree) and silently returns
// null. We query from the component's own root node instead — pass any
// element that lives inside the checkout DOM as the anchor (e.g. the step's
// bound wrapper); getRootNode() resolves to the shadow root (or document in
// light-DOM/test contexts).
//
// Covers both client-side (validate()) and server-side (e.g. "account
// exists") errors — the only requirement is that the error node is rendered
// with one of the known markers by the time the next animation frame runs.

const ERROR_SELECTOR = '.text-scarlet, [data-checkout-error], [aria-invalid="true"]';

/**
 * @param {Element|null|undefined} anchorEl  any element inside the checkout DOM
 * @param {string} [selector]  override the error selector if needed
 */
export function scrollToFirstError(anchorEl, selector = ERROR_SELECTOR) {
    if (typeof window === 'undefined') return;
    const root = anchorEl?.getRootNode?.()
        ?? (typeof document !== 'undefined' ? document : null);
    if (!root?.querySelector) return;

    // Defer a frame so Svelte has flushed the (possibly just-set) error nodes
    // into the DOM before we look for them.
    window.requestAnimationFrame(() => {
        const el = root.querySelector(selector);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // a11y: move focus to the related form control so screen readers
        // announce the field; fall back to the error node itself (made
        // focusable) when there's no input nearby (top-level errors).
        const group = el.closest('div') ?? el;
        const focusable = group.querySelector?.(
            'input, select, textarea, button:not([type="button"]), [tabindex]'
        );
        const target = focusable ?? el;
        if (target === el && !el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
        }
        try { target.focus?.({ preventScroll: true }); } catch (_) { /* noop */ }
    });
}
